/* =============================================================================
   map.js — Mapa de ubicación
   Hotel La Gruta · Arequipa

   Por qué Leaflet y no Mapbox
   ---------------------------
   Mapbox exige un token en el frontend. Aunque un token público (`pk.`) esté
   pensado para ir ahí, el escáner de secretos de GitHub lo bloquea en cada
   push, y además ata el sitio a una cuenta y a una cuota. Leaflet con tiles
   públicas no necesita token, ni cuenta, ni facturación, y pesa unas 20 veces
   menos que Mapbox GL.

   La librería va vendorizada en assets/vendor/leaflet/ (BSD-2) en vez de
   cargarse de un CDN: el sitio tiene que poder subirse tal cual a cualquier
   hosting sin depender de que un tercero siga sirviendo el archivo.

   Se inicializa BAJO DEMANDA desde app.js (`window.initHomeMap`, disparado
   por un IntersectionObserver sobre `.home-map`) y no al cargar: un mapa
   creado dentro de un contenedor oculto/fuera de viewport nace con tamaño 0
   y se ve roto.

   La página de Ubicación (que tenía su propio mapa a pantalla completa,
   `window.initLocMap`/`#loc-map-canvas`) se retiró del sitio por pedido
   del cliente — ver ARQUITECTURA.md. El menú también tuvo su propia
   miniatura de mapa (`window.initMenuMap`/`#menu-map-canvas`) hasta que se
   volvió redundante con el mapa de Inicio y se quitó — ver ARQUITECTURA.md.
   Ahora solo queda el mapa de Inicio.
   ============================================================================= */
(function () {
  'use strict';

  var LAT = -16.387764, LNG = -71.530067;

  // Proveedor de tiles en un solo sitio: cambiarlo es tocar este objeto y
  // nada más.
  //
  // Se usó CARTO (basemaps.cartocdn.com) hasta que dejó de servir tiles
  // anónimas y empezó a pedir cuenta + API key — exactamente lo que se quería
  // evitar al dejar Mapbox (ver arriba). Se probó también Esri World Light
  // Gray Canvas (el mapa "gris" clásico de Leaflet), pero en el mapa grande
  // de Inicio quedaba tan pálido que apenas se distinguía como mapa — casi
  // no se notaba calles ni manzanas, solo gris plano. World Street Map (del
  // mismo servidor Esri, mismo esquema sin key ni cuenta) trae calles,
  // parques y edificios en color y ya incluye sus propias etiquetas, así
  // que acá va en una sola capa (antes eran dos: base + labels transparente).
  // Si algún día también cierran el acceso anónimo, la alternativa sin key
  // más robusta es el servidor oficial de OpenStreetMap
  // (tile.openstreetmap.org), con su propia política de uso — no pensada
  // para tráfico alto sin tile server propio.
  //
  // OJO con el orden de {z}/{y}/{x}: Esri usa el esquema ArcGIS REST
  // (nivel/fila/columna), NO el {z}/{x}/{y} habitual de XYZ.
  var TILES = {
    base: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 19,
    attribution: 'Tiles &copy; <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a> &middot; ' +
                 'Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCan, Esri Japan, METI, Esri China (Hong Kong), ' +
                 '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors, GIS User Community'
  };

  /* ── Recorrido Hotel → Plaza de Armas ───────────────────────────────────────
     Los puntos viven en assets/data/ruta-plaza.json (calcados una sola vez de
     la línea azul de referencia): así el mapa no depende de ningún servicio
     de rutas en tiempo de ejecución.
     La línea es la ruta exacta elegida por el hotel (ver "fuente" en el JSON).
     Si el archivo no carga, se dibuja una recta hotel → plaza en vez de nada. */
  var HOTEL = [LAT, LNG];
  var VERDE = '#1B5A1B';            // el verde del logo (mismo que luz.css .lc-sub)
  var DASH = 10, GAP = 9;           // px en pantalla; se recalculan en cada zoom
  var REVEAL_MS = 2800;             // el trazo se escribe entero en este tiempo
  var FADE_MS = 900;                // fundido de cada rayita (debe coincidir con .ruta-raya en site.css)
  var HOLD_MS = 2400;               // con el trazo completo, antes de desvanecerlo y reiniciar
  var FRAME_PAD = 0.10;             // margen del encuadre, como fracción del lado menor del mapa
  var RM = matchMedia('(prefers-reduced-motion: reduce)').matches;

  function tr(key, fallback) { return (window.I18N && window.I18N.t(key)) || fallback; }

  function cargarRuta() {
    var aprox = { hotel: HOTEL, plaza: [-16.39889, -71.53694], ruta: null };
    aprox.ruta = [aprox.hotel, aprox.plaza];
    return fetch('/assets/data/ruta-plaza.json')
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (d) { return d && d.ruta && d.ruta.length > 1 ? d : aprox; })
      .catch(function () { return aprox; });
  }

  // Iconos en línea (sin peticiones extra, heredan el color).
  // Catedral de Arequipa: fachada con dos torres, frontón central y portada en arco.
  var SVG_CATEDRAL = '<svg viewBox="0 0 28 24" width="26" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 21h24"/><path d="M4 21V10l2.4-3.4L8.8 10v11"/><path d="M19.2 21V10l2.4-3.4L24 10v11"/><path d="M6.4 6.6V4.6M21.6 6.6V4.6M5.6 5.6h1.6M20.8 5.6h1.6"/><path d="M8.8 21v-8l5.2-3.6 5.2 3.6v8"/><path d="M14 9.4V7.4M13.2 8.3h1.6"/><path d="M12 21v-4.2a2 2 0 0 1 4 0V21"/></svg>';
  // Lugares de interés (ids = puntos[].id del JSON). Trazo de línea como los
  // demás, en 24x24 para que entren en una insignia pequeña.
  var SVG_POI = {
    'santa-catalina': '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21h18"/><path d="M3 11.5 12 6l9 5.5"/><path d="M5 21v-6.5a1.8 1.8 0 0 1 3.6 0V21M10.2 21v-6.5a1.8 1.8 0 0 1 3.6 0V21M15.4 21v-6.5a1.8 1.8 0 0 1 3.6 0V21"/></svg>',                       // claustro de arcos: Monasterio de Santa Catalina
    'san-francisco': '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 21h16"/><path d="M6 21v-9l6-4 6 4v9"/><path d="M12 8V3M10 5h4"/><path d="M10 21v-4.2a2 2 0 0 1 4 0V21"/></svg>',                                              // fachada con cruz: Iglesia de San Francisco
    'campo-redondo': '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 9h11v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9Z"/><path d="M16 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3M5 9a3 3 0 0 1 0-6 3 3 0 0 1 2 .8 3 3 0 0 1 5.2-.2A3 3 0 0 1 16 6v3M8.5 12v6M12.5 12v6"/></svg>',                       // jarra de cerveza: bares de Campo Redondo
    'bares': '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 11h11v3.5a4.5 4.5 0 0 1-4.5 4.5h-2A4.5 4.5 0 0 1 5 14.5z"/><path d="M16 12h1.4a2.3 2.3 0 0 1 0 4.6H16"/><path d="M8.5 3.5c-1 1.3 1 2.2 0 3.6M12.5 3.5c-1 1.3 1 2.2 0 3.6"/><path d="M4 21h13"/></svg>'                   // taza: bares y cafeterías
  };
  function iconoPoi(p) {
    return L.divIcon({
      className: 'mk mk-poi', iconSize: [32, 32], iconAnchor: [16, 16],
      html: '<div class="mk-in"><span class="mk-badge">' + (SVG_POI[p.id] || '') + '</span>' +
            '<span class="mk-label mk-l-' + (p.lado || 'abajo') + '"><span class="mk-txt" data-poi="' + p.id + '">' + tr('homemap.poi.' + p.id, p.id) + '</span></span></div>'
    });
  }
  var SVG_MIRA = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="6.2"/><circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none"/><path d="M12 2.5v3.3M12 18.2v3.3M2.5 12h3.3M18.2 12h3.3"/></svg>';

  function iconoHotel() {
    // Logo completo (La Gruta · HOTEL · ★★★) en verde, en una insignia de
    // esquinas redondeadas: es más alto que el wordmark y una píldora lo
    // recortaría. Ya dice "La Gruta", así que no lleva etiqueta aparte.
    return L.divIcon({
      className: 'mk mk-hotel', iconSize: [96, 66], iconAnchor: [48, 33],
      html: '<div class="mk-in"><span class="mk-badge"><img src="/assets/img/logos/optim/lagruta-seal-192.png" alt="La Gruta Hotel"></span></div>'
    });
  }
  function iconoPlaza() {
    return L.divIcon({
      className: 'mk mk-plaza', iconSize: [46, 46], iconAnchor: [23, 23],
      html: '<div class="mk-in"><span class="mk-badge">' + SVG_CATEDRAL + '</span>' +
            '<span class="mk-label"><span class="mk-txt">' + tr('homemap.plaza', 'Plaza de Armas') + '</span></span></div>'
    });
  }
  // Mapa grande de Inicio (ver .home-map en site.css). Interactivo
  // (arrastre + zoom con botones), salvo la rueda del mouse, que en NINGÚN
  // mapa del sitio hace zoom — para no secuestrar el scroll de la página.
  var iniciando = false;
  window.initHomeMap = function () {
    var host = document.getElementById('home-map-canvas');
    if (window.homeMap || iniciando || !window.L || !host) return;
    iniciando = true;
    cargarRuta().then(function (datos) { construir(host, datos); });
  };

  function construir(host, datos) {
    var ruta = datos.ruta, plaza = datos.plaza, hotel = datos.hotel || HOTEL;
    var marco = host.closest('.home-map-frame');

    var map = L.map(host, {
      center: hotel,
      zoom: 15,
      zoomSnap: 0.25,          // el encuadre se ajusta a los dos puntos, no a niveles enteros
      zoomControl: false,
      scrollWheelZoom: false,
      dragging: true,
      tap: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: false,
      attributionControl: true
    });

    L.tileLayer(TILES.base, { maxZoom: TILES.maxZoom, attribution: TILES.attribution }).addTo(map);

    /* ── Encuadre ──
       fitBounds con margen proporcional: hotel y plaza quedan protagonistas,
       no pegados al borde. En escritorio la tarjeta tapa parte derecha del
       mapa, así que ese tramo se suma al margen: el encuadre se centra en
       lo que SÍ se ve. */
    var enMarcha = false, movido = false;
    function solape() {
      var card = document.querySelector('.home-map-card');
      // Solo cuenta si la tarjeta flota SOBRE el mapa (escritorio). En móvil
      // va debajo (position: static) aunque se solape 1px con el marco.
      if (!card || !marco || getComputedStyle(card).position !== 'absolute') return 0;
      var c = card.getBoundingClientRect(), m = marco.getBoundingClientRect();
      if (c.width === 0 || c.left >= m.right || c.bottom <= m.top || c.top >= m.bottom) return 0;
      return Math.max(0, m.right - c.left);
    }
    function encuadrar(puntos, animar) {
      var tam = map.getSize(), pad = Math.round(Math.min(tam.x, tam.y) * FRAME_PAD), der = solape();
      // La atribución (obligatoria por licencia) ocupa 1 línea en escritorio
      // y hasta 3 en móvil: el margen inferior la descuenta para que no tape
      // la etiqueta de la plaza. Esa etiqueta cuelga ~58px bajo su punto
      // (medio ícono + separación + píldora): también se suma.
      var atr = marco && marco.querySelector('.leaflet-control-attribution'), abajo = pad + (atr ? atr.offsetHeight : 0) + 58;
      enMarcha = true;
      map.fitBounds(L.latLngBounds(puntos), {
        // Arriba se suma medio logo del hotel (iconAnchor 33): en un marco bajo
        // quedaba pegado al borde.
        paddingTopLeft: [pad, pad + 33], paddingBottomRight: [pad + der, abajo],
        maxZoom: 17, animate: !!animar && !RM, duration: 0.9
      });
    }
    var vistaBase = ruta.concat([hotel, plaza]);
    encuadrar(vistaBase, false);
    map.on('movestart', function () { if (!enMarcha) movido = true; });
    map.on('moveend', function () { enMarcha = false; });

    /* ── Recorrido punteado ──
       Se parte la ruta en rayitas de DASH px separadas GAP px, calculadas en
       pixeles del zoom actual (un stroke-dasharray único no permite animar
       cada rayita por separado). Al cambiar el zoom se vuelve a partir. */
    var capa = L.layerGroup().addTo(map), rayas = [], revelado = false;
    function partir(zoom) {
      var P = ruta.map(function (ll) { return map.project(L.latLng(ll), zoom); });
      var out = [], cur = [map.unproject(P[0], zoom)], dibujando = true, resta = DASH;
      for (var i = 0; i < P.length - 1; i++) {
        var a = P[i], b = P[i + 1], len = a.distanceTo(b), pos = 0;
        while (len - pos > 1e-6) {
          var paso = Math.min(resta, len - pos);
          pos += paso; resta -= paso;
          var pt = map.unproject(L.point(a.x + (b.x - a.x) * pos / len, a.y + (b.y - a.y) * pos / len), zoom);
          if (dibujando) cur.push(pt);
          if (resta <= 1e-6) {
            if (dibujando) { out.push(cur); cur = []; } else { cur = [pt]; }
            dibujando = !dibujando; resta = dibujando ? DASH : GAP;
          }
        }
      }
      if (dibujando && cur.length > 1) out.push(cur);
      return out;
    }
    function dibujar() {
      capa.clearLayers(); rayas = [];
      partir(map.getZoom()).forEach(function (pts) {
        var l = L.polyline(pts, { className: 'ruta-raya', color: VERDE, weight: 3.4, opacity: 1,
                                  lineCap: 'round', lineJoin: 'round', interactive: false });
        l.addTo(capa); rayas.push(l);
      });
    }
    /* Bucle: el trazo se escribe rayita a rayita desde el hotel, se sostiene
       un momento con la Catedral a la vista, se desvanece entero y vuelve a
       empezar. Con movimiento reducido queda fijo y visible, sin bucle. */
    var timers = [], enPantalla = false;
    function parar() { timers.forEach(clearTimeout); timers = []; }
    function ciclo() {
      parar();
      if (!revelado || !rayas.length) return;
      var els = rayas.map(function (l) { return l.getElement(); }).filter(Boolean);
      if (RM) { els.forEach(function (el) { el.style.transition = 'none'; el.classList.add('on'); }); return; }
      var paso = Math.min(70, REVEAL_MS / els.length);
      els.forEach(function (el) { el.style.transition = 'none'; el.classList.remove('on'); });
      void host.offsetWidth;   // fija opacity:0 antes de animar, o no hay fundido
      els.forEach(function (el, i) {
        el.style.transition = ''; el.style.transitionDelay = (i * paso) + 'ms'; el.classList.add('on');
      });
      timers.push(setTimeout(function () {
        els.forEach(function (el) { el.style.transitionDelay = '0ms'; el.classList.remove('on'); });
        timers.push(setTimeout(ciclo, FADE_MS + 250));
      }, REVEAL_MS + FADE_MS + HOLD_MS));
    }
    dibujar();
    function etiquetasPoi() { if (marco) marco.classList.toggle('poi-zoom', map.getZoom() >= 16); }
    etiquetasPoi();
    map.on('zoomend', function () { etiquetasPoi(); dibujar(); if (enPantalla) ciclo(); });

    /* ── Marcadores ── */
    var mHotel = L.marker(hotel, { interactive: false, keyboard: false, icon: iconoHotel() }).addTo(map);
    var mPlaza = L.marker(plaza, { interactive: false, keyboard: false, icon: iconoPlaza() }).addTo(map);
    var mPois = (datos.puntos || []).map(function (p) {
      return L.marker([p.lat, p.lng], { interactive: false, keyboard: false, icon: iconoPoi(p) }).addTo(map);
    });
    function mostrar(m, demora) {
      var el = m.getElement(); if (!el) return;
      el.style.setProperty('--mk-delay', (RM ? 0 : demora) + 'ms');
      requestAnimationFrame(function () { el.classList.add('mk-show'); });
    }
    document.addEventListener('i18n:change', function () {
      var t = mPlaza.getElement() && mPlaza.getElement().querySelector('.mk-txt');
      if (t) t.textContent = tr('homemap.plaza', 'Plaza de Armas');
      (datos.puntos || []).forEach(function (p, i) {
        var e = mPois[i] && mPois[i].getElement(), x = e && e.querySelector('.mk-txt');
        if (x) x.textContent = tr('homemap.poi.' + p.id, p.id);
      });
      textosControl();
    });

    // El recorrido se "escribe" cuando el mapa entra en pantalla, no al crearlo
    // (se crea 200px antes de verse): primero el hotel, luego el trazo
    // rayita a rayita y, al llegar, la Catedral.
    function revelar() {
      if (revelado) return;
      revelado = true;
      mostrar(mHotel, 0);
      mostrar(mPlaza, RM ? 0 : REVEAL_MS);
      mPois.forEach(function (m, i) { mostrar(m, 700 + i * 380); });
      ciclo();
    }
    if ('IntersectionObserver' in window && !RM) {
      // Se mantiene observando: el bucle se pausa fuera de pantalla y arranca
      // de cero al volver, en vez de gastar CPU sin que nadie lo vea.
      var primera = true;
      new IntersectionObserver(function (es) {
        enPantalla = es[0].isIntersecting;
        if (!enPantalla) { parar(); return; }
        if (primera) { primera = false; timers.push(setTimeout(revelar, 350)); } else ciclo();
      }, { threshold: 0.45 }).observe(marco || host);
    } else { revelar(); }

    /* ── Botón de centrado ──
       La mira devuelve el mapa al encuadre hotel + plaza (por si el visitante
       arrastró o hizo zoom). No pide la ubicación del visitante: para eso
       está "Cómo llegar", que abre Google Maps. */
    var botonEl = null;
    function textosControl() {
      if (!botonEl) return;
      var txt = tr('homemap.recenter', 'Centrar en el hotel y la Plaza de Armas');
      botonEl.setAttribute('aria-label', txt); botonEl.title = txt;
    }
    var Centrar = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd: function () {
        var cont = L.DomUtil.create('div', 'leaflet-bar leaflet-control locate-ctl');
        var a = botonEl = L.DomUtil.create('a', 'locate-btn', cont);
        a.href = '#'; a.setAttribute('role', 'button'); a.innerHTML = SVG_MIRA;
        textosControl();
        L.DomEvent.disableClickPropagation(cont);
        L.DomEvent.on(a, 'click', function (e) {
          L.DomEvent.preventDefault(e);
          movido = false;
          encuadrar(vistaBase, true);
        });
        return cont;
      }
    });
    // Se añade ANTES que el zoom: en las esquinas inferiores Leaflet apila lo
    // último arriba, y se quiere + / − arriba y la mira debajo.
    new Centrar().addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    window.homeMap = map;
    window.homeMapReencuadrar = function () {
      map.invalidateSize();
      if (!movido) encuadrar(vistaBase, false);
    };
    requestAnimationFrame(function () { map.invalidateSize(); if (!movido) encuadrar(vistaBase, false); });
  }

  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(function () {
      if (window.homeMapReencuadrar) window.homeMapReencuadrar();
    }, 300);
  }, { passive: true });
})();
